import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import analyticsService from '../services/analyticsService';
import useSEO from '../hooks/useSEO';
import { categoryUrl, courseUrl } from '../utils/catalogUrls';
import '../styles/SearchResults.css';

function SearchResults() {
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search).get('q')?.trim() || '', [location.search]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useSEO({
    title: query ? `Busca por ${query} | ABRALIBER` : 'Buscar Cursos | ABRALIBER',
    description: 'Busque cursos online gratuitos na ABRALIBER por nome, área ou categoria.',
    url: query ? `/buscar?q=${encodeURIComponent(query)}` : '/buscar',
  });

  useEffect(() => {
    if (query.length < 2) {
      setResults(null);
      return;
    }

    const controller = new AbortController();
    let active = true;

    const fetchResults = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await apiService.search(query, { signal: controller.signal });
        if (!active) return;
        setResults(response.data);
        const data = response.data;
        const resultCount = (data.cursos?.length || 0)
          + (data.relacionados?.length || 0)
          + (data.categorias?.length || 0);
        analyticsService.trackSearch(
          query,
          data.match_found === false ? 0 : resultCount,
          data.match_found === false ? 'search_results_fallback' : 'search_results',
        );
      } catch {
        if (active) setError('Não foi possível carregar os resultados da busca.');
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchResults();

    return () => {
      active = false;
      controller.abort();
    };
  }, [query]);

  const cursos = results?.cursos || [];
  const relacionados = results?.relacionados || [];
  const categorias = results?.categorias || [];
  const isFallback = results?.match_found === false;
  const hasResults = cursos.length > 0 || relacionados.length > 0 || categorias.length > 0;

  return (
    <main className="search-results-page">
      <section className="search-results-hero">
        <div className="search-results-container">
          <h1>Buscar cursos na ABRALIBER</h1>
          <form className="search-results-form" action="/buscar" method="get">
            <input
              type="search"
              name="q"
              defaultValue={query}
              placeholder="Digite o nome do curso ou categoria"
              aria-label="Buscar cursos"
            />
            <button type="submit">Buscar</button>
          </form>
        </div>
      </section>

      <section className="search-results-content">
        <div className="search-results-container">
          {query.length < 2 && (
            <p className="search-results-message">Digite pelo menos dois caracteres para buscar cursos.</p>
          )}

          {loading && <p className="search-results-message">Buscando cursos...</p>}
          {error && <p className="search-results-message error">{error}</p>}

          {!loading && isFallback && hasResults && !error && (
            <div className="search-results-suggestion" role="status">
              <strong>
                {results.suggestion_message || 'Curso não encontrado, mas você pode gostar desses cursos!'}
              </strong>
              <span>Selecionamos opções relacionadas ao tema e cursos populares da plataforma.</span>
            </div>
          )}

          {!loading && query.length >= 2 && !hasResults && !error && (
            <p className="search-results-message">Nenhum resultado encontrado para "{query}".</p>
          )}

          {categorias.length > 0 && (
            <div className="search-results-group">
              <h2>{isFallback ? 'Categorias relacionadas' : 'Categorias'}</h2>
              <div className="search-category-list">
                {categorias.map((categoria) => (
                  <Link key={categoria.id} to={categoryUrl(categoria)} className="search-category-item">
                    {categoria.nome}
                    <span>{categoria.cursos_count} cursos</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {cursos.length > 0 && (
            <div className="search-results-group">
              <h2>Cursos</h2>
              <div className="search-course-list">
                {cursos.map((curso) => (
                  <Link key={curso.id} to={courseUrl(curso)} className="search-course-item" data-analytics-course-id={curso.id} data-analytics-source="search_results">
                    {curso.imagem_url && <img src={curso.imagem_url} alt="" loading="lazy" />}
                    <span>{curso.title}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {relacionados.length > 0 && (
            <div className="search-results-group">
              <h2>{isFallback ? 'Cursos que você pode gostar' : 'Cursos Relacionados'}</h2>
              <div className="search-course-list">
                {relacionados.map((curso) => (
                  <Link key={curso.id} to={courseUrl(curso)} className="search-course-item" data-analytics-course-id={curso.id} data-analytics-source="search_results">
                    {curso.imagem_url && <img src={curso.imagem_url} alt="" loading="lazy" />}
                    <span>{curso.title}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

export default SearchResults;
